import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Box,
  Alert,
  Chip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EuroIcon from '@mui/icons-material/Euro';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import axios from 'axios';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import TerrainOutlinedIcon from '@mui/icons-material/TerrainOutlined';
import { countries } from '../data/locations';
import { useAuth } from '../contexts/AuthContext';
import { C } from '../theme';
import MediaPlaceholder from '../components/MediaPlaceholder';

interface LandAd {
  id: number;
  title: string;
  description: string;
  price: number;
  country_id: string;
  city_id: string;
  thumbnail: string | null;
  land_type: string;
  area: number;
  author_name: string;
  created_at: string;
  type: 'land';
}

interface BusinessAd {
  id: number;
  title: string;
  description: string;
  price: number;
  country_id: string;
  city_id: string;
  thumbnail: string | null;
  business_type: string;
  annual_revenue?: number;
  employee_count?: number;
  established_year?: number;
  author_name: string;
  created_at: string;
  type: 'business';
}

type Ad = LandAd | BusinessAd;

const landTypeLabelMap: Record<string, string> = {
  agricultural: 'Poljoprivredno zemljište',
  residential: 'Gradsko zemljište',
  commercial: 'Šumsko zemljište',
  industrial: 'Zemljište za izgradnju',
  recreational: 'Zemljište za rekreaciju',
};

const businessTypeLabelMap: Record<string, string> = {
  restaurant_hospitality: 'Restoran i ugostiteljstvo',
  retail_services: 'Trgovina i usluge',
  manufacturing: 'Proizvodnja',
  technology_it: 'Tehnologija i IT',
  healthcare: 'Zdravstvo',
  education: 'Obrazovanje',
  transport_logistics: 'Transport i logistika',
  real_estate: 'Nekretnine',
  finance_banking: 'Finansije i bankarstvo',
  other: 'Ostalo',
};

const MyAds: React.FC = () => {
  const [ads, setAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    fetchMyAds();
  }, [isAuthenticated, navigate]);

  const fetchMyAds = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const [landAdsResponse, businessAdsResponse] = await Promise.all([
        axios.get('/api/ads/my-ads', { headers: { Authorization: `Bearer ${token}` } }),
        axios.get('/api/business-ads/user/me', { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const landAds = landAdsResponse.data.map((ad: any) => ({ ...ad, type: 'land' as const }));
      const businessAds = businessAdsResponse.data.map((ad: any) => ({ ...ad, type: 'business' as const }));

      const allAds = [...landAds, ...businessAds].sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      setAds(allAds);
    } catch (error) {
      console.error('Error fetching ads:', error);
      setError('Greška pri učitavanju vaših oglasa');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (ad: Ad) => {
    if (!window.confirm('Da li ste sigurni da želite da obrišete ovaj oglas?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');

      if (ad.type === 'land') {
        await axios.delete(`/api/ads/${ad.id}`, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.delete(`/api/business-ads/${ad.id}`, { headers: { Authorization: `Bearer ${token}` } });
      }

      setAds(ads.filter((a) => a.id !== ad.id));
    } catch (error) {
      console.error('Error deleting ad:', error);
      setError('Greška pri brisanju oglasa');
    }
  };

  const getLocationName = (ad: Ad) => {
    const country = countries.find(c => c.id === Number(ad.country_id));
    const city = country?.cities.find(c => c.id === Number(ad.city_id));
    return `${city?.name || ''}, ${country?.name || ''}`;
  };

  const getTypeLabel = (ad: Ad) => {
    if (ad.type === 'land') {
      return landTypeLabelMap[ad.land_type] || ad.land_type;
    } else {
      return businessTypeLabelMap[ad.business_type] || ad.business_type;
    }
  };

  const getTypeColor = (ad: Ad): 'primary' | 'success' => (ad.type === 'land' ? 'primary' : 'success');

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh" sx={{ backgroundColor: C.bg }}>
        <CircularProgress sx={{ color: C.gold }} />
      </Box>
    );
  }

  return (
    <Box sx={{ pt: '110px', pb: 8, backgroundColor: C.bg, minHeight: '100vh' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" sx={{ fontWeight: 800, mb: 2, color: C.white }}>
            Moji oglasi
          </Typography>
          <Typography variant="h6" sx={{ color: C.muted, maxWidth: 600, mx: 'auto', lineHeight: 1.6, fontWeight: 400 }}>
            Upravljajte vašim oglasima za zemljište i biznis, pratećite njihovu vidljivost
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {ads.length === 0 ? (
          <Box textAlign="center" sx={{ py: 8 }}>
            <Typography variant="h6" sx={{ mb: 2, color: C.white }}>
              Nemate objavljenih oglasa
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: C.muted }}>
              Započnite sa kreiranjem vašeg prvog oglasa za zemljište ili biznis
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={4}>
            {ads.map((ad) => (
              <Grid item key={ad.id} xs={12} sm={6} lg={4}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 4,
                    backgroundColor: C.surface,
                    border: `1px solid ${C.line}`,
                    transition: 'all 0.3s cubic-bezier(0.16,1,0.3,1)',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      borderColor: 'rgba(243,192,21,0.35)',
                      boxShadow: '0 16px 36px rgba(0,0,0,0.5)',
                    },
                  }}
                >
                  {ad.thumbnail ? (
                    <CardMedia
                      component="img"
                      height="200"
                      image={ad.type === 'land'
                        ? `/api/ads/uploads/thumbnails/${ad.thumbnail}`
                        : `/uploads/thumbnails/${ad.thumbnail}`}
                      alt={ad.title}
                      sx={{ objectFit: 'cover' }}
                    />
                  ) : (
                    <MediaPlaceholder
                      height={200}
                      icon={(ad.type === 'land' ? <TerrainOutlinedIcon sx={{ fontSize: 48, color: 'rgba(243,192,21,0.45)' }} /> : <StorefrontOutlinedIcon sx={{ fontSize: 48, color: 'rgba(243,192,21,0.45)' }} />)}
                      label="Bez fotografije"
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        fontWeight: 700,
                        color: C.white,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {ad.title}
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={ad.type === 'land' ? 'Zemljište' : 'Biznis'}
                        color={getTypeColor(ad)}
                        size="small"
                        sx={{ fontWeight: 600, borderRadius: 1, mb: 1 }}
                      />
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Chip
                        icon={<EuroIcon sx={{ color: `${C.onGold} !important` }} />}
                        label={`${ad.price.toLocaleString()} €`}
                        sx={{ background: C.goldGrad, color: C.onGold, fontWeight: 800, borderRadius: 2, fontSize: '0.875rem', mb: 1 }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <LocationOnIcon sx={{ color: C.gold, mr: 0.5, fontSize: '1rem' }} />
                        <Typography variant="body2" sx={{ color: C.muted, fontWeight: 500 }}>
                          {getLocationName(ad)}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{
                        color: C.muted,
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {ad.description}
                    </Typography>

                    <Grid container spacing={1} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: C.muted, mb: 0.5 }}>
                          Tip
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: C.white }}>
                          {getTypeLabel(ad)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" sx={{ color: C.muted, mb: 0.5 }}>
                          {ad.type === 'land' ? 'Površina' : 'Kategorija'}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: C.white }}>
                          {ad.type === 'land' ? `${ad.area.toLocaleString()} m²` : getTypeLabel(ad)}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Typography variant="body2" sx={{ color: C.muted, fontSize: '0.75rem' }}>
                      Objavljeno: {new Date(ad.created_at).toLocaleDateString('sr-RS')}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 3, pt: 0, justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(ad.type === 'land' ? `/ads/${ad.id}` : `/business/${ad.id}`)}
                        sx={{ color: C.white, '&:hover': { color: C.gold, backgroundColor: C.goldSoft } }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => navigate(ad.type === 'land' ? `/edit-ad/${ad.id}` : `/edit-business-ad/${ad.id}`)}
                        sx={{ color: C.white, '&:hover': { color: C.gold, backgroundColor: C.goldSoft } }}
                        title="Uredi oglas"
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(ad)}
                      sx={{ color: C.danger, '&:hover': { backgroundColor: 'rgba(255,90,90,0.12)' } }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {ads.length > 0 && (
          <Box sx={{ textAlign: 'center', mt: 6 }}>
            <Typography variant="body1" sx={{ color: C.muted }}>
              Ukupno oglasa: {ads.length}
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default MyAds;
